// Registering the field service worker (PRD 95 §4.13) — only from the field
// shell (FieldShell), with scope `/business/field/`, so the rest of the app is
// never controlled by it; production builds only.
//
// A new version never takes over by itself: when one has installed and is
// waiting, subscribers hear `updateReady`, and the page shows "A new version is
// ready — Reload". *Reload* tells the waiting worker to take over, and the page
// reloads once it has.
//
// THE KILL SWITCH: a build with REACT_APP_SERVICE_WORKER=off registers nothing
// and unregisters any field worker already installed, so a bad worker is undone
// by one deploy.

import { FIELD_SCOPE } from './fieldWorkerRoutes';

type Listener = (ready: boolean) => void;

let registration: ServiceWorkerRegistration | null = null;
let started = false;
let updateReady = false;
const listeners = new Set<Listener>();

const announce = (ready: boolean) => {
  updateReady = ready;
  listeners.forEach(listener => listener(ready));
};

export const serviceWorkerSwitchedOff = (): boolean => process.env.REACT_APP_SERVICE_WORKER === 'off';

const supported = (): boolean =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && process.env.NODE_ENV === 'production';

/** Watches a registration for a worker that has installed and is waiting. */
function watch(reg: ServiceWorkerRegistration): void {
  if (reg.waiting && navigator.serviceWorker.controller) announce(true);
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    installing?.addEventListener('statechange', () => {
      // A first install has no controller to replace: nothing to announce.
      if (installing.state === 'installed' && navigator.serviceWorker.controller) announce(true);
    });
  });
}

/** Idempotent: the first field page to mount registers; the rest find it done. */
export async function startFieldServiceWorker(): Promise<void> {
  if (started || !supported()) return;
  started = true;
  if (serviceWorkerSwitchedOff()) {
    const all = await navigator.serviceWorker.getRegistrations();
    await Promise.all(all.filter(reg => reg.scope.endsWith(FIELD_SCOPE)).map(reg => reg.unregister()));
    return;
  }
  try {
    registration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`, { scope: FIELD_SCOPE });
    watch(registration);
  } catch {
    // No worker is no offline shell, never a broken page.
    started = false;
  }
}

/** The person pressed Reload: the waiting worker takes over, then the page reloads. */
export function applyFieldUpdate(): void {
  const waiting = registration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function onFieldUpdate(listener: Listener): () => void {
  listeners.add(listener);
  listener(updateReady);
  return () => {
    listeners.delete(listener);
  };
}

/** For tests. */
export function resetFieldServiceWorker(): void {
  registration = null;
  started = false;
  updateReady = false;
  listeners.clear();
}
